
import { supabase } from "@/integrations/supabase/client";

/**
 * Deletes a client and returns whether the operation was successful
 */
export const deleteClient = async (clientId: string) => {
  try {
    // Get the current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error("You must be logged in to delete a client");
    }

    // Get client name before deletion for activity log
    const { data: clientData, error: clientError } = await supabase
      .from('clients')
      .select('full_name')
      .eq('client_id', clientId)
      .single();

    if (clientError) {
      console.error("Error fetching client details:", clientError);
      throw new Error("Failed to fetch client details. Please try again.");
    }

    const clientName = clientData?.full_name || 'Unknown client';

    // First, delete any associated programs
    const { error: programsDeleteError } = await supabase
      .from('programs')
      .delete()
      .eq('client_id', clientId);

    if (programsDeleteError) {
      console.error("Error deleting associated programs:", programsDeleteError);
      throw new Error("Failed to delete associated programs. Please try again.");
    }

    // Then delete the client
    const { error } = await supabase
      .from('clients')
      .delete()
      .eq('client_id', clientId)
      .eq('coach_id', user.id);

    if (error) {
      console.error("Error deleting client:", error);
      throw new Error("Failed to delete client. Please try again.");
    }

    // Create an activity entry for the client removal
    const { error: activityError } = await supabase
      .from('activities')
      .insert({
        user_id: user.id,
        type: 'new_client', // Using 'new_client' type for consistency
        title: 'Client Removed',
        description: `Removed client: ${clientName}`,
        data: { client_id: clientId, client_name: clientName, action: 'removed' }
      });

    if (activityError) {
      console.error("Error creating activity log:", activityError);
      // We don't throw here to avoid blocking the client deletion flow
    }

    return true;
  } catch (error) {
    console.error("Error in deleteClient:", error);
    throw error;
  }
};
